import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertifyService } from '@liebherr/old-sage-ui';
import { EMPTY } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { IamTenantAPI } from '../api/core';
import { AppState } from '../app.models';
import { finalizeEffect } from '../shared/operators/finalize-effect';
import * as TenantActions from './tenants.actions';
import { TenantActionTypes } from './tenants.models';

@Injectable()
export class TenantEffects {
    load$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TenantActions.loadTenants),
            tap(() => this.store.dispatch(TenantActions.loading())),
            switchMap((action) => {
                return this.api.getAllTenants().pipe(
                    map((res) => {
                        return TenantActions.loadedTenants({ data: res });
                    }),
                    finalizeEffect(
                        () => this.store.dispatch(TenantActions.loadingDone()),
                        () =>
                            this.translateService
                                .selectTranslate('ALERT.LOADING_FAILED')
                                .pipe(first())
                                .subscribe((message) => this.alertifyService.log(message, AlertifyService.ERROR, 'bottom left')),
                        TenantActionTypes.LOADED_TENANTS
                    )
                );
            })
        );
    });
    loadDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TenantActions.loadTenantDetails),
            tap(() => this.store.dispatch(TenantActions.loading())),
            switchMap((action) => {
                return this.api.getTenantById({ id: action.keycloakId }).pipe(
                    map((res) => {
                        return TenantActions.loadedTenantDetails({
                            tenant: res,
                            keycloakId: action.keycloakId,
                        });
                    }),
                    finalizeEffect(
                        () => this.store.dispatch(TenantActions.loadingDone()),
                        () =>
                            this.translateService
                                .selectTranslate('ALERT.LOADING_FAILED')
                                .pipe(first())
                                .subscribe((message) => this.alertifyService.log(message, AlertifyService.ERROR, 'bottom left')),
                        TenantActionTypes.LOADED_TENANT_DETAILS
                    )
                );
            })
        );
    });

    create$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TenantActions.createTenant),
            tap(() => this.store.dispatch(TenantActions.loading())),
            switchMap((action) => {
                return this.api.postNewTenant({ body: action.tenant }).pipe(
                    map((res) => {
                        this.alertifyService.log(
                            this.translateService.translate('ALERT.CREATE_SUCCESS'),
                            AlertifyService.SUCCESS,
                            'bottom left'
                        );
                        return TenantActions.createdTenant({ tenant: res });
                    }),
                    finalizeEffect(
                        () => this.store.dispatch(TenantActions.loadingDone()),
                        () =>
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.CREATE_FAILED'),
                                AlertifyService.ERROR,
                                'bottom left'
                            ),
                        TenantActionTypes.CREATED_TENANT
                    )
                );
            })
        );
    });

    update$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TenantActions.updateTenant),
            tap(() => this.store.dispatch(TenantActions.loading())),
            switchMap((action) => {
                if (action.tenant.id) {
                    return this.api.putById({ id: action.tenant.id, body: action.tenant }).pipe(
                        map(() => {
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.UPDATE_SUCCESS'),
                                AlertifyService.SUCCESS,
                                'bottom left'
                            );
                            return TenantActions.updatedTenant({ tenant: action.tenant });
                        }),
                        finalizeEffect(
                            () => this.store.dispatch(TenantActions.loadingDone()),
                            () =>
                                this.alertifyService.log(
                                    this.translateService.translate('ALERT.UPDATE_FAILED'),
                                    AlertifyService.ERROR,
                                    'bottom left'
                                ),
                            TenantActionTypes.UPDATED_TENANT
                        )
                    );
                } else {
                    return EMPTY.pipe(finalizeEffect(() => this.store.dispatch(TenantActions.loadingDone())));
                }
            })
        );
    });
    delete$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(TenantActions.deleteTenant),
            tap(() => this.store.dispatch(TenantActions.loading())),
            switchMap((action) => {
                return this.api.deleteTenantById({ id: action.id }).pipe(
                    map(() => {
                        this.alertifyService.log(
                            this.translateService.translate('ALERT.DELETE_SUCCESS'),
                            AlertifyService.SUCCESS,
                            'bottom left'
                        );
                        return TenantActions.deletedTenant({ id: action.id });
                    }),
                    finalizeEffect(
                        () => this.store.dispatch(TenantActions.loadingDone()),
                        () =>
                            this.alertifyService.log(
                                this.translateService.translate('ALERT.DELETE_FAILED'),
                                AlertifyService.ERROR,
                                'bottom left'
                            ),
                        TenantActionTypes.DELETED_TENANT
                    )
                );
            })
        );
    });

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private alertifyService: AlertifyService,
        private translateService: TranslocoService,
        private api: IamTenantAPI
    ) {}
}
